/* The Record tab's derived columns (Ale rulings 2026-09-03, C2; the flat
 * table of 2026-09-05 keeps them — see positionLedger.ts for the order,
 * the ledger box and the counts).
 *
 *   BALANCE — the last confirmed balance on the record plus the confirmed
 *             movements dated after it; with no dated balance, the sum of
 *             the movements; a commitment package shows its committed
 *             amount (drawn / undrawn are its own lines).
 *   AS OF   — a DATE only: the date of the latest confirmed item that
 *             fixes the balance (the balance's own date, or the last
 *             movement folded into it).
 *   EV      — evidenced / needed over the VERTICAL SET that confirms the
 *             position from its holder: the position (a commitment package
 *             counts as ONE object — both records' demands, one set), its
 *             instrument, and the POSITION's trades. Issuances belong to
 *             the instrument and are not counted here (Ale 2026-09-03:
 *             trade counts are position trades only). Waived = proven.
 *   RECO    — 0, or the delta the reconciliation engine found on the
 *             record (units: record minus what the movements say). The
 *             engine decides whether a break exists; the tab only reports
 *             its figure. A figure other than 0 is the door to the task.
 *
 * Pure (no imports) so the bare node runner can prove the arithmetic —
 * see tests/unit/recordDerive.test.mjs. Figures are formatted here by the
 * officer rule (thousands grouped, 2dp only when real) with no currency:
 * the currency is the CCY column's. */

export interface DeriveSegment {
  position_id: string
  position_ids: string[]
  instrument_id: string | null
  instrument_name: string | null
  issuer_name: string | null
  units_held: number | null
  committed_amount: number | null
  drawn_amount: number | null
  undrawn_amount: number | null
  as_of: string | null
}

export interface DeriveTrade {
  id: string
  position_id: string | null
  instrument_id: string | null
  units_delta: number | null
  effective_date: string | null
  state?: string
}

export interface DeriveMatrixRow {
  object_id: string
  status: string
}

export interface DeriveTask {
  task_id: string
  task_type: string
  target_id: string | null
  task_data?: unknown
}

export const RECON_TASK_TYPES = new Set([
  'position_trade_missing',
  'position_balance_stale',
  'position_balance_rollforward',
  'position_leg_identity_violation',
  'instrument_trade_missing',
  'instrument_balance_stale',
  'commitment_supply_mismatch',
])

const confirmed = (t: DeriveTrade): boolean => t.state !== 'proposed'

/** The leg's own record ids — one for a plain position, two for a
 *  commitment package (the commitment and the undrawn record). */
export function legPositionIds(seg: DeriveSegment): string[] {
  const ids = seg.position_ids.length ? [...seg.position_ids] : []
  if (!ids.includes(seg.position_id)) ids.unshift(seg.position_id)
  return ids
}

/** Confirmed trades on any record of the leg, oldest first. */
export function tradesOfLeg(seg: DeriveSegment, trades: DeriveTrade[]): DeriveTrade[] {
  const ids = new Set(legPositionIds(seg))
  return trades
    .filter((t) => confirmed(t) && t.position_id !== null && ids.has(t.position_id))
    .sort((a, b) => (a.effective_date ?? '').localeCompare(b.effective_date ?? ''))
}

export type BalanceSource = 'paper' | 'paper+trades' | 'trades' | 'record' | 'none'

export interface Balance {
  figure: number | null
  asOf: string | null
  source: BalanceSource
  /** How many confirmed movements were folded into the figure. */
  folded: number
}

function latestDate(dates: (string | null)[]): string | null {
  let best: string | null = null
  for (const d of dates) if (d && (!best || d > best)) best = d
  return best
}

/** A partial movement sum is not a balance. */
function completeMovementSum(trades: DeriveTrade[]): number | null {
  let sum = 0
  for (const trade of trades) {
    if (trade.units_delta === null) return null
    sum += trade.units_delta
  }
  return sum
}

/** THE BALANCE RULE. */
export function balanceOf(seg: DeriveSegment, trades: DeriveTrade[]): Balance {
  const legs = tradesOfLeg(seg, trades)
  if (seg.committed_amount !== null) {
    // The committed amount is a stated fact; drawdown movements belong to
    // the separate drawn/undrawn figures, not an addition to commitment.
    return {
      figure: seg.committed_amount,
      asOf: latestDate([seg.as_of, ...legs.map((t) => t.effective_date)]),
      source: 'record',
      folded: 0,
    }
  }
  if (seg.as_of) {
    const later = legs.filter((t) => (t.effective_date ?? '') > (seg.as_of as string))
    const movementSum = completeMovementSum(later)
    if (seg.units_held === null || movementSum === null) {
      // Keep the last stated figure and its original date. Do not turn an
      // absent starting balance into zero or date a partial sum as current.
      return {
        figure: seg.units_held,
        asOf: seg.as_of,
        source: seg.units_held === null ? 'none' : 'paper',
        folded: 0,
      }
    }
    return {
      figure: seg.units_held + movementSum,
      asOf: latestDate([seg.as_of, ...later.map((t) => t.effective_date)]),
      source: later.length ? 'paper+trades' : 'paper',
      folded: later.length,
    }
  }
  const movementSum = completeMovementSum(legs)
  if (legs.length && movementSum !== null) {
    return {
      figure: movementSum,
      asOf: latestDate(legs.map((t) => t.effective_date)),
      source: 'trades',
      folded: legs.length,
    }
  }
  return {
    figure: seg.units_held,
    asOf: null,
    source: seg.units_held === null ? 'none' : 'record',
    folded: 0,
  }
}

export interface Fraction {
  proven: number
  total: number
}

/** The object ids of the vertical set: the leg's records, its instrument,
 *  and the position's trades. */
export function verticalSetIds(seg: DeriveSegment, trades: DeriveTrade[]): Set<string> {
  const ids = new Set<string>(legPositionIds(seg))
  if (seg.instrument_id) ids.add(seg.instrument_id)
  for (const t of tradesOfLeg(seg, trades)) ids.add(t.id)
  return ids
}

/** THE EV RULE: evidenced / needed over the vertical set. */
export function evOf(
  seg: DeriveSegment,
  trades: DeriveTrade[],
  matrix: DeriveMatrixRow[]
): Fraction {
  const ids = verticalSetIds(seg, trades)
  let proven = 0
  let total = 0
  for (const r of matrix) {
    if (!ids.has(r.object_id)) continue
    total += 1
    if (r.status === 'satisfied' || r.status === 'waived') proven += 1
  }
  return { proven, total }
}

export interface Reco {
  /** 0 when the engine holds no break on these records. */
  delta: number
  task: DeriveTask | null
}

function num(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null
  const n = typeof v === 'number' ? v : Number(String(v))
  return Number.isFinite(n) ? n : null
}

/** The delta a reconciliation task carries: the diagnosis's delta, else
 *  the expected movement, else stored minus computed. */
export function taskDelta(task: DeriveTask): number | null {
  const data = (task.task_data ?? {}) as Record<string, unknown>
  const diag = (data.diagnosis ?? {}) as Record<string, unknown>
  const direct = num(diag.delta) ?? num(data.expected_units_delta)
  if (direct !== null) return direct
  const stored = num(diag.stored)
  const computed = num(diag.computed)
  if (stored !== null && computed !== null) return stored - computed
  return null
}

/** The records whose delta a POSITION row may carry: its own position
 *  records only (Ale 2026-09-03). An INSTRUMENT-level break — the paper's
 *  issued total against the sum of its holdings (B3) — is not a position
 *  delta: it belongs on the instrument's own line (the instrument drawer's
 *  OWED), never in a position row's RECO. */
export function rowRecoIds(seg: DeriveSegment): string[] {
  return legPositionIds(seg)
}

/** THE RECO RULE for a set of record ids (one record of a package, or a
 *  plain position's record). */
export function recoOf(objectIds: string[], tasks: DeriveTask[]): Reco {
  const ids = new Set(objectIds)
  const task = tasks.find(
    (t) => RECON_TASK_TYPES.has(t.task_type) && t.target_id !== null && ids.has(t.target_id)
  )
  if (!task) return { delta: 0, task: null }
  return { delta: taskDelta(task) ?? 0, task }
}

/* ── formatting: the officer figure rule, no currency ──────────────────── */

export function figure(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—'
  const rounded = Math.round(v * 100) / 100
  const hasFraction = Math.abs(rounded - Math.trunc(rounded)) > 0
  return rounded.toLocaleString('en-US', {
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: hasFraction ? 2 : 0,
  })
}

/** A signed delta: "0", "−400", "+1,000" (typographic minus). */
export function deltaWords(delta: number): string {
  if (delta === 0) return '0'
  return delta < 0 ? `−${figure(-delta)}` : `+${figure(delta)}`
}

/** INSTRUMENT never wraps: over `max` characters the name keeps its first
 *  words and its last twelve characters, folding the middle. */
export function foldMiddle(name: string, max = 34, tail = 12): string {
  if (name.length <= max) return name
  const end = name.slice(-tail)
  const budget = max - tail - 1 // the ellipsis
  const words = name.split(' ')
  let head = words[0]
  for (let i = 1; i < words.length; i += 1) {
    const next = `${head} ${words[i]}`
    if (next.length > budget) break
    head = next
  }
  if (head.length > budget) head = head.slice(0, Math.max(1, budget))
  return `${head}…${end}`
}
